import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import {
  Coins, Star, Swords, Library, Layers, Gem, ShieldCheck,
  Sparkles, TrendingUp, Zap, ChevronRight, User, LogOut, Clock, Map,
} from "lucide-react";

/** Format `seconds_left` into a compact "1h 12m" / "12m 04s" / "58s" tag so
 *  time-limited buffs (Bountiful Charm) render their live countdown next to
 *  the badge without spilling out of the hub. */
function formatCountdown(sec) {
  if (typeof sec !== "number" || sec <= 0) return null;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, "0")}s`;
  return `${s}s`;
}

const RARITY_COLORS = {
  common: "#9ca3af", uncommon: "#34d399", rare: "#38bdf8",
  epic: "#a78bfa", legendary: "#fbbf24", goddess: "#f472b6",
};
const RARITY_ORDER = ["common", "uncommon", "rare", "epic", "legendary", "goddess"];

function StatTile({ icon: Icon, label, value, color = "#22d3ee", testid }) {
  return (
    <div className="rounded-lg border border-white/8 bg-white/[0.03] px-2.5 py-2" data-testid={testid}>
      <div className="flex items-center gap-1 text-white/40">
        <Icon className="h-3 w-3" style={{ color }} />
        <span className="font-mono text-[8px] uppercase tracking-[0.18em]">{label}</span>
      </div>
      <div className="mt-0.5 font-display text-base text-white leading-none tabular-nums">{value}</div>
    </div>
  );
}

function RarityBar({ byRarity, total }) {
  if (!total) return null;
  return (
    <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-white/5" data-testid="player-hub-rarity-bar">
      {RARITY_ORDER.map((r) => {
        const n = byRarity[r] || 0;
        if (!n) return null;
        return <div key={r} style={{ width: `${(n / total) * 100}%`, background: RARITY_COLORS[r] }} title={`${r}: ${n}`} />;
      })}
    </div>
  );
}

export function PlayerHub({ children, className }) {
  const [open, setOpen] = useState(false);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { data, dataUpdatedAt } = useQuery({
    queryKey: ["me-hub"],
    queryFn: async () => (await api.get("/me/hub")).data,
    enabled: open,
    staleTime: 15000,
  });

  const p = data?.player;
  const c = data?.collection;
  const g = data?.progression;
  const cb = data?.combat;
  const rawBuffs = useMemo(() => data?.buffs || [], [data?.buffs]);
  const isAdmin = p?.role === "admin";

  // Freeze the fetched-at timestamp per query response so the visible
  // countdown ticks locally without re-fetching. The buff seconds we
  // subtract are (now - fetchedAt).
  const fetchedAt = dataUpdatedAt;
  const [nowTs, setNowTs] = useState(() => Date.now());
  useEffect(() => {
    if (!open) return undefined;
    const anyTimed = rawBuffs.some((b) => typeof b?.seconds_left === "number" && b.seconds_left > 0);
    if (!anyTimed) return undefined;
    const t = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [open, rawBuffs]);

  const buffs = rawBuffs.map((b) => {
    if (typeof b?.seconds_left !== "number") return b;
    const elapsed = Math.floor((nowTs - fetchedAt) / 1000);
    return { ...b, seconds_left: Math.max(0, b.seconds_left - elapsed) };
  }).filter((b) => typeof b?.seconds_left !== "number" || b.seconds_left > 0);

  return (
    <HoverCard open={open} onOpenChange={setOpen} openDelay={120} closeDelay={120}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent
        side="bottom"
        align="start"
        sideOffset={12}
        data-testid="player-hub"
        className={`w-[340px] p-0 overflow-hidden border-white/12 bg-[hsl(222_26%_7%)] shadow-[0_24px_70px_rgba(0,0,0,0.7)] z-[90] ${className || ""}`}
      >
        {/* Header */}
        <div className="relative px-4 pt-4 pb-3 border-b border-white/8"
          style={{ background: "linear-gradient(135deg, rgba(34,211,238,0.10), rgba(167,139,250,0.06) 60%, transparent)" }}>
          <div className="flex items-center gap-3">
            <div className="relative h-11 w-11 rounded-xl grid place-items-center font-display text-lg text-black shrink-0"
              style={{ background: "linear-gradient(135deg,#22d3ee,#a78bfa)", boxShadow: "0 0 18px rgba(34,211,238,0.45)" }}>
              {(p?.display_name || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <div className="font-display text-base text-white truncate tracking-wide" data-testid="player-hub-name">
                  {p?.display_name || "—"}
                </div>
                {isAdmin && (
                  <span className="inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-[0.15em] text-teal-300 bg-teal-400/10 border border-teal-400/30">
                    <ShieldCheck className="h-2.5 w-2.5" /> Admin
                  </span>
                )}
              </div>
              <div className="mt-0.5 inline-flex items-center gap-1 font-mono text-sm text-amber-300 tabular-nums" data-testid="player-hub-credits">
                <Coins className="h-3.5 w-3.5" /> {(p?.credits ?? 0).toLocaleString()}
                <span className="text-white/30 text-[10px] ml-0.5">credits</span>
              </div>
            </div>
          </div>
        </div>

        {/* Active buffs */}
        <div className="px-4 py-3 border-b border-white/8" data-testid="player-hub-buffs">
          <div className="flex items-center gap-1.5 mb-2">
            <Zap className="h-3 w-3 text-emerald-300" />
            <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/45">Active Buffs</span>
          </div>
          {buffs.length === 0 ? (
            <Link to="/path" data-testid="player-hub-no-buffs"
              className="block rounded-lg border border-dashed border-white/12 px-3 py-2 text-[11px] text-white/45 hover:text-white/70 hover:border-white/25 transition-colors">
              No active buffs yet — unlock perks on <span className="text-yellow-300">The Path</span> →
            </Link>
          ) : (
            <div className="space-y-1.5">
              {buffs.map((b) => {
                const countdown = formatCountdown(b.seconds_left);
                return (
                  <div key={b.key} data-testid={`player-hub-buff-${b.key}`}
                    className="flex items-center gap-2.5 rounded-lg border px-2.5 py-1.5"
                    style={{ borderColor: `${b.color}40`, background: `${b.color}12` }}>
                    <Sparkles className="h-3.5 w-3.5 shrink-0" style={{ color: b.color }} />
                    <div className="min-w-0 flex-1">
                      <div className="font-display text-[13px] text-white leading-tight">{b.label}</div>
                      <div className="font-mono text-[10px] text-white/45 truncate">{b.desc}</div>
                    </div>
                    <div className="flex flex-col items-end shrink-0 gap-0.5">
                      <span className="font-mono text-sm font-bold" style={{ color: b.color }}>{b.value}</span>
                      {countdown && (
                        <span
                          className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.14em] text-white/60"
                          data-testid={`player-hub-buff-${b.key}-countdown`}
                          title="Time remaining"
                        >
                          <Clock className="h-2.5 w-2.5" style={{ color: b.color }} />
                          {countdown}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Vitals */}
        <div className="px-4 py-3">
          <div className="grid grid-cols-3 gap-1.5">
            <StatTile icon={Library} label="Cards" value={(c?.total_cards ?? 0).toLocaleString()} color="#a78bfa" testid="player-hub-cards" />
            <StatTile icon={Layers} label="Designs" value={(c?.unique_designs ?? 0).toLocaleString()} color="#38bdf8" testid="player-hub-designs" />
            <StatTile icon={Gem} label="Tiers" value={`${c?.tiers_collected ?? 0}/${c?.tiers_total ?? 6}`} color="#f472b6" testid="player-hub-tiers" />
            <StatTile icon={TrendingUp} label="Est. Value" value={(c?.est_value ?? 0).toLocaleString()} color="#fbbf24" testid="player-hub-value" />
            <StatTile icon={Swords} label="Arena" value={`${cb?.wins ?? 0}-${cb?.losses ?? 0}`} color="#fb7185" testid="player-hub-arena" />
            <StatTile icon={Star} label="Skill Pts" value={(g?.sp ?? 0).toLocaleString()} color="#fde047" testid="player-hub-sp" />
          </div>
          <div className="mt-2.5">
            <RarityBar byRarity={c?.by_rarity || {}} total={c?.total_cards || 0} />
          </div>
        </div>

        {/* Footer → Path */}
        <Link to="/path" data-testid="player-hub-path-link"
          className="flex items-center justify-between gap-2 px-4 py-2.5 border-t border-white/8 bg-white/[0.02] hover:bg-white/[0.05] transition-colors group">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
            Path · {g?.nodes_unlocked ?? 0}/{g?.nodes_total ?? 0} unlocked
          </span>
          <span className="inline-flex items-center gap-1 text-[11px] text-yellow-300 group-hover:translate-x-0.5 transition-transform">
            View Path <ChevronRight className="h-3 w-3" />
          </span>
        </Link>

        {/* Account actions — profile shortcut + sign out + roadmap. Kept as
         *  flat rows so they're easy to hit on mobile even with the hub scaled
         *  down. Roadmap sits above the profile/sign-out row so it's obvious
         *  to every player where community proposals live. */}
        <Link
          to="/roadmap"
          data-testid="player-hub-roadmap-link"
          onClick={() => setOpen(false)}
          className="flex items-center justify-between gap-2 px-3 py-2.5 text-[11px] font-mono uppercase tracking-[0.22em] text-cyan-200 hover:text-cyan-100 hover:bg-cyan-400/[0.06] border-t border-white/8 transition-colors"
        >
          <span className="flex items-center gap-1.5">
            <Map className="h-3.5 w-3.5" /> Roadmap · propose ideas
          </span>
          <ChevronRight className="h-3 w-3 text-cyan-300/70" />
        </Link>
        <div className="grid grid-cols-2 border-t border-white/8">
          <Link
            to="/profile"
            data-testid="player-hub-profile-link"
            onClick={() => setOpen(false)}
            className="flex items-center justify-center gap-1.5 px-3 py-2.5 text-[11px] font-mono uppercase tracking-[0.22em] text-white/65 hover:text-cyan-300 hover:bg-white/[0.04] transition-colors"
          >
            <User className="h-3.5 w-3.5" /> Profile
          </Link>
          <button
            type="button"
            data-testid="player-hub-logout"
            onClick={async () => { if (await logout()) { setOpen(false); navigate("/"); } }}
            className="flex items-center justify-center gap-1.5 px-3 py-2.5 text-[11px] font-mono uppercase tracking-[0.22em] text-white/65 hover:text-red-300 hover:bg-red-400/5 border-l border-white/8 transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
